import React, { useState, useEffect, useMemo } from 'react';
import {
  Button,
  Input,
  Modal,
  Select,
  DatePicker,
  Typography,
  Radio,
} from 'antd';
import moment from 'moment';
// Constants
import {
  MAX_DESCRIPTION_LENGTH,
  MAX_TITLE_LENGTH,
} from '../../TimelineDeliverablesModal';
// Helpers
import {
  generateContentOnlyDefaultFormData,
  mapUgcEditingItemToFormData,
  mapContentOnlyFormDataToCreateCampaignContentTaskInput,
  ContentOnlyModalFormData,
  DUE_DATE_OPTIONS,
  SINGLE_DATE_TYPE_OPTIONS,
} from 'helpers/campaignUgc';
import {
  createNumberOptions,
  getApprovalUnitOptions,
  findNumberOption,
  getDisabledDate,
} from 'helpers/timelineDeliverables';
import { ValueLabelOption } from 'helpers/campaignEditModal';
// Types
import { CreateCampaignContentTaskInput } from 'api/graphql-global-types';
import { GetCampaignsV2_getCampaigns_entities_activities_UgcActivity_tasks } from 'api/campaignV2/types/GetCampaignsV2';
import {
  CampaignMediaType,
  CbDueDateType,
  CbDisplayPeriod,
} from 'api/graphql-global-types';
// Styles
import styles from './ContentOnlyModal.module.scss';
import { errorNotification } from 'ui/Notification';

const { TextArea } = Input;
const { Option } = Select;

type ContentOnlyModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateCampaignContentTaskInput) => void;
  editingItem?: GetCampaignsV2_getCampaigns_entities_activities_UgcActivity_tasks | null;
};

const ContentOnlyModal: React.FC<ContentOnlyModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  editingItem,
}) => {
  const contentTaskOptions: ValueLabelOption[] = useMemo(
    () => [
      { label: 'Video', value: CampaignMediaType.VIDEO },
      { label: 'Image', value: CampaignMediaType.IMAGE },
    ],
    []
  );

  const approvalUnitOptions: ValueLabelOption[] = useMemo(
    () => getApprovalUnitOptions(),
    []
  );

  const defaultFormData = useMemo(
    () => generateContentOnlyDefaultFormData(),
    []
  );

  const initialValues: Partial<ContentOnlyModalFormData> = useMemo(() => {
    return {
      ...defaultFormData,
      ...(editingItem ? mapUgcEditingItemToFormData(editingItem) : {}),
    } as ContentOnlyModalFormData;
  }, [editingItem, defaultFormData]);

  const [formData, setFormData] = useState<ContentOnlyModalFormData>(
    initialValues as ContentOnlyModalFormData
  );

  // Reset form when modal opens/closes or editingItem changes
  useEffect(() => {
    if (isOpen) {
      setFormData(initialValues as ContentOnlyModalFormData);
    }
  }, [isOpen, initialValues]);

  const handleCloseModal = () => {
    setFormData(defaultFormData as ContentOnlyModalFormData);
    onClose();
  };

  const updateFormData = (
    field: keyof ContentOnlyModalFormData,
    value: any
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const validateForm = (): boolean => {
    if (!formData.mediaType) {
      errorNotification('Please select a content type');
      return false;
    }
    if (!formData.title?.trim()) {
      errorNotification('Please enter a content title');
      return false;
    }
    if (!formData.description?.trim()) {
      errorNotification('Please enter a description');
      return false;
    }
    if (!formData.dueDateType) {
      errorNotification('Please select a due date type');
      return false;
    }

    // Validate application-based timeline
    if (formData.dueDateType === CbDueDateType.APPLICATION_BASED) {
      if (!formData.approvalNumber) {
        errorNotification('Please select an approval number');
        return false;
      }
      if (!formData.approvalUnit) {
        errorNotification('Please select an approval unit');
        return false;
      }
    }

    // Validate specific date
    if (formData.dueDateType === 'specific') {
      if (!formData.dateRangeType) {
        errorNotification('Please select fixed date or range');
        return false;
      }

      if (formData.dateRangeType === CbDueDateType.FIXED) {
        if (!formData.singleDateType) {
          errorNotification('Please select a date type (Before/On)');
          return false;
        }
        if (!formData.dueDate) {
          errorNotification('Please select a due date');
          return false;
        }
      }

      if (formData.dateRangeType === CbDueDateType.RANGE) {
        if (!formData.startDate) {
          errorNotification('Please select a start date');
          return false;
        }
        if (!formData.endDate) {
          errorNotification('Please select an end date');
          return false;
        }
      }
    }

    return true;
  };

  const handleSave = () => {
    if (!validateForm()) {
      return;
    }

    const mappedData =
      mapContentOnlyFormDataToCreateCampaignContentTaskInput(formData);
    onSubmit(mappedData);
    handleCloseModal();
  };

  const dueDateType = formData.dueDateType;
  const dateRangeType = formData.dateRangeType;
  const approvalUnit = formData.approvalUnit;
  const approvalNumber = formData.approvalNumber;
  const dueDateNotice = `Content is due ${approvalNumber} ${
    (approvalUnit as string)?.toLowerCase()?.slice(0, -1) +
    ((approvalNumber as number) > 1 ? 's' : '')
  } after approval`;

  const approvalNumberOptions: ValueLabelOption[] = useMemo(() => {
    return createNumberOptions(approvalUnit === CbDisplayPeriod.DAYS ? 6 : 4);
  }, [approvalUnit]);

  return (
    <Modal
      title="Content Deliverable"
      visible={isOpen}
      onCancel={handleCloseModal}
      width={560}
      footer={null}
      className={styles.contentOnlyModal}
    >
      <div className={styles.modalContent}>
        <div className={styles.formGroup}>
          <Typography.Text strong>Content Type</Typography.Text>
          <Select
            value={formData.mediaType ?? CampaignMediaType.VIDEO}
            onChange={(value) => updateFormData('mediaType', value)}
            placeholder="Select type"
            style={{ width: '100%' }}
          >
            {contentTaskOptions.map((opt) => (
              <Option key={opt.value} value={opt.value}>
                {opt.label}
              </Option>
            ))}
          </Select>
          <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
            Only include one content item per deliverable.
          </Typography.Text>
        </div>

        <div className={styles.formGroup}>
          <Typography.Text strong>Content Title</Typography.Text>
          <Input
            value={formData.title ?? ''}
            onChange={(e) => updateFormData('title', e.target.value)}
            placeholder="Example: Make an unboxing video"
            maxLength={MAX_TITLE_LENGTH}
          />
        </div>

        <div className={styles.formGroup}>
          <Typography.Text strong>Description</Typography.Text>
          <TextArea
            value={formData.description ?? ''}
            onChange={(e) => updateFormData('description', e.target.value)}
            rows={4}
            placeholder="Be specific and thorough—include your goal/idea/vision including any scripts, suggested talking points, dos and don'ts, tone or style guidance, settings, tips, or important context. The more direction you provide, the better the final result will match your expectations."
            maxLength={MAX_DESCRIPTION_LENGTH}
          />
          <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
            Describe what you&apos;re expecting the Talent to do for this
            deliverable.
          </Typography.Text>
        </div>

        <div className={styles.formGroup}>
          <Typography.Text strong>Content Due Date</Typography.Text>
          <Select
            value={dueDateType}
            onChange={(value) => updateFormData('dueDateType', value)}
            placeholder="Select Content Deadline"
            style={{ width: '100%' }}
          >
            {DUE_DATE_OPTIONS.map((opt) => (
              <Option key={String(opt.value)} value={opt.value}>
                {opt.label}
              </Option>
            ))}
          </Select>
          {dueDateType === CbDueDateType.APPLICATION_BASED && (
            <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
              {dueDateNotice}
            </Typography.Text>
          )}
        </div>

        {dueDateType === CbDueDateType.APPLICATION_BASED && (
          <div className={styles.rowWrapper}>
            <div className={styles.formGroup}>
              <Typography.Text strong>Number</Typography.Text>
              <Select
                value={
                  findNumberOption(approvalNumberOptions, approvalNumber)?.value
                }
                onChange={(value) =>
                  updateFormData('approvalNumber', Number(value))
                }
                placeholder="Select number"
                style={{ width: '100%' }}
              >
                {approvalNumberOptions.map((option) => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
            </div>
            <div className={styles.formGroup}>
              <Typography.Text strong>Unit</Typography.Text>
              <Select
                value={approvalUnit}
                onChange={(value) => {
                  updateFormData('approvalUnit', value);
                  // Reset approvalNumber when unit changes
                  updateFormData('approvalNumber', undefined);
                }}
                placeholder="Select unit"
                style={{ width: '100%' }}
              >
                {approvalUnitOptions.map((option) => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
            </div>
          </div>
        )}

        {dueDateType === 'specific' && (
          <>
            <div className={styles.formGroup}>
              <Typography.Text strong>Date Type</Typography.Text>
              <Radio.Group
                value={dateRangeType}
                onChange={(e) =>
                  updateFormData('dateRangeType', e.target.value)
                }
              >
                <Radio value={CbDueDateType.FIXED}>Fixed Date</Radio>
                <Radio value={CbDueDateType.RANGE}>Range</Radio>
              </Radio.Group>
            </div>

            {dateRangeType === CbDueDateType.FIXED && (
              <div className={styles.rowWrapper}>
                <div className={styles.formGroup}>
                  <Typography.Text strong>Timing</Typography.Text>
                  <Select
                    value={formData.singleDateType}
                    onChange={(value) =>
                      updateFormData('singleDateType', value)
                    }
                    placeholder="Select timing"
                    style={{ width: '100%' }}
                  >
                    {SINGLE_DATE_TYPE_OPTIONS.map((option) => (
                      <Option key={option.value} value={option.value}>
                        {option.label}
                      </Option>
                    ))}
                  </Select>
                </div>
                <div className={styles.formGroup}>
                  <Typography.Text strong>Date</Typography.Text>
                  <DatePicker
                    value={formData.dueDate ? moment(formData.dueDate) : null}
                    onChange={(date) =>
                      updateFormData('dueDate', date?.toDate())
                    }
                    placeholder="MM/DD/YYYY"
                    style={{ width: '100%' }}
                    disabledDate={getDisabledDate}
                    format="MMMM D, YYYY"
                  />
                </div>
              </div>
            )}

            {dateRangeType === CbDueDateType.RANGE && (
              <div className={styles.rowWrapper}>
                <div className={styles.formGroup}>
                  <Typography.Text strong>Start Date</Typography.Text>
                  <DatePicker
                    value={
                      formData.startDate ? moment(formData.startDate) : null
                    }
                    onChange={(date) =>
                      updateFormData('startDate', date?.toDate())
                    }
                    placeholder="Start Date"
                    style={{ width: '100%' }}
                    disabledDate={(current) =>
                      current && current < moment().startOf('day')
                    }
                    format="MMMM D, YYYY"
                  />
                </div>
                <div className={styles.formGroup}>
                  <Typography.Text strong>End Date</Typography.Text>
                  <DatePicker
                    value={formData.endDate ? moment(formData.endDate) : null}
                    onChange={(date) =>
                      updateFormData('endDate', date?.toDate())
                    }
                    placeholder="End Date"
                    style={{ width: '100%' }}
                    disabledDate={(current) => {
                      if (!current) return false;
                      // Disable dates before today
                      if (current < moment().startOf('day')) return true;
                      // Disable dates before start date
                      if (
                        formData.startDate &&
                        current < moment(formData.startDate).startOf('day')
                      ) {
                        return true;
                      }
                      return false;
                    }}
                    format="MMMM D, YYYY"
                  />
                </div>
              </div>
            )}
          </>
        )}

        <div className={styles.actions}>
          <Button onClick={handleCloseModal}>Back</Button>
          <Button type="primary" onClick={handleSave}>
            Save
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ContentOnlyModal;
